/**
 * Flavor text setting.
 *
 * The in-universe layer — agency notices, contract jargon, euphemism weapon
 * names, bot chatter — is optional and off by default, so the game speaks in
 * plain names. Static DOM carries both versions: the plain text as content and
 * the fiction on a `data-flavor` attribute; `applyFlavorDom` swaps them.
 * Dynamic strings go through `fx(plain, flavor)`.
 *
 * Persisted per browser (`orbituary-flavor-text`). Never affects game state,
 * so it is safe to differ between P2P peers.
 */

import type { WeaponKey, ItemKey } from '../types';
import { WEAPONS, HEAL_ITEMS } from '../constants';
import { getGcppaMessage } from '../lore/flavor';

const STORAGE_KEY = 'orbituary-flavor-text';

function load(): boolean {
  try { return localStorage.getItem(STORAGE_KEY) === '1'; } catch { return false; }
}

let enabled = load();
const listeners = new Set<() => void>();

export function isFlavorEnabled(): boolean { return enabled; }

export function setFlavorEnabled(v: boolean): void {
  enabled = v;
  try { localStorage.setItem(STORAGE_KEY, v ? '1' : '0'); } catch { /* storage unavailable */ }
  applyFlavorDom();
  updateFlavorToggles();
  for (const l of listeners) l();
}

/** Called after the setting changes; returns an unsubscribe. */
export function onFlavorChange(cb: () => void): () => void {
  listeners.add(cb);
  return () => { listeners.delete(cb); };
}

/** Pick the text for the current setting. */
export function fx(plain: string, flavor: string): string { return enabled ? flavor : plain; }

/** The currency's display name. */
export function cur(): string { return enabled ? 'CBP' : 'credits'; }

export function weaponDisplayName(key: WeaponKey): string {
  const w = WEAPONS[key];
  return enabled ? w.flavorName : w.name;
}

export function itemDisplayName(key: ItemKey): string {
  const item = HEAL_ITEMS[key];
  return enabled ? item.flavorName : item.name;
}

/** Match-referee notifications (deaths, win, draw): plain line or GCPPA notice. */
export function matchMessage(type: 'death' | 'gameOver' | 'draw', vars: Record<string, string>): string {
  return getGcppaMessage(type, vars, undefined, !enabled);
}
export function matchSender(): string { return fx('MATCH', 'GCPPA'); }
export function alertSender(): string { return fx('ALERT', 'TACCOM'); }

/** Swap every `[data-flavor]` element's text and every `[data-cur]` currency label. */
export function applyFlavorDom(root: ParentNode = document): void {
  for (const el of Array.from(root.querySelectorAll<HTMLElement>('[data-flavor]'))) {
    if (el.dataset.plain === undefined) el.dataset.plain = el.textContent ?? '';
    el.textContent = enabled ? (el.dataset.flavor ?? '') : el.dataset.plain;
  }
  for (const el of Array.from(root.querySelectorAll<HTMLElement>('[data-cur]'))) el.textContent = cur();
  if (typeof document !== 'undefined') document.title = fx('Orbital Artillery', 'GCPPA Contract Portal');
}

/** Every flavor toggle button (menu and in-game) shows the current state. */
export function updateFlavorToggles(): void {
  for (const el of Array.from(document.querySelectorAll<HTMLElement>('.setting-flavor'))) {
    el.textContent = enabled ? 'ON' : 'OFF';
    el.classList.toggle('off', !enabled);
  }
}
